/**
 * M15g metinleri (ADR 0062): ortak kayıt PDF'i (`recordPdf`), HSE sağlık verisi ve PDF'i
 * (`hse.medical`, `hse.pdf`), ofisin şirket uygunsuzluğu (`companyNc`) ve liman araması PDF'i
 * (`portCalls.pdf`). İki dilde aynı anahtarlar (`locales.test.ts`).
 */

/** Şirket uygunsuzluğu ekranının alabildiği hata kodları. */
export const COMPANY_NC_ERROR_CODES = [
  'GENERIC',
  'NETWORK',
  'UNAUTHENTICATED',
  'FORBIDDEN',
  'NOT_FOUND',
  'VALIDATION_FAILED',
  'ROW_VERSION_CONFLICT',
  'INVALID_COMPANY_NC',
  'INVALID_COMPANY_NC_ACTION',
  'COMPANY_NC_CLOSED',
  'COMPANY_NC_NOT_CLOSED',
  'COMPANY_NC_SELF_CLOSE',
  'COMPANY_NC_NOT_CLOSABLE',
  'COMPLETION_NOTE_REQUIRED',
  'REASON_REQUIRED',
  'REAUTHENTICATION_REQUIRED',
  'INVALID_PASSWORD',
  'TOO_MANY_ATTEMPTS',
  'HSE_ACTION_FINISHED',
  'INVALID_ACTION_TRANSITION',
] as const;

export const recordPdfEn = {
  number: 'No: {{no}}',
  approval: 'Approval and record trail',
  noApproval: 'No approval step recorded yet.',
  step: 'Step',
  name: 'Name',
  role: 'Role',
  time: 'Time (LT / UTC)',
  note: 'Note',
  approvalNote:
    'Recorded electronically in Felixmate; the approval record replaces a handwritten signature.',
  printed: 'Printed {{at}}',
  page: 'Page {{page}} / {{pages}}',
  restricted: 'Restricted',
  draftMark: 'DRAFT – NOT SUBMITTED',
  reopenedMark: 'REOPENED',
  closedMark: 'CLOSED',
  restrictedMark: 'MEDICAL DETAILS RESTRICTED',
  print: 'Print / PDF',
  printing: 'Preparing PDF…',
  error: 'The PDF could not be produced.',
};

export const recordPdfTr: typeof recordPdfEn = {
  number: 'No: {{no}}',
  approval: 'Onay ve kayıt izi',
  noApproval: 'Henüz onay adımı kaydedilmedi.',
  step: 'Adım',
  name: 'Ad',
  role: 'Rol',
  time: 'Zaman (LT / UTC)',
  note: 'Not',
  approvalNote: 'Felixmate’da elektronik olarak kaydedildi; onay kaydı ıslak imzanın yerine geçer.',
  printed: 'Basım {{at}}',
  page: 'Sayfa {{page}} / {{pages}}',
  restricted: 'Gizli',
  draftMark: 'TASLAK – GÖNDERİLMEDİ',
  reopenedMark: 'YENİDEN AÇILDI',
  closedMark: 'KAPATILDI',
  restrictedMark: 'SAĞLIK BİLGİSİ GİZLİ',
  print: 'Yazdır / PDF',
  printing: 'PDF hazırlanıyor…',
  error: 'PDF oluşturulamadı.',
};

export const hseExtraEn = {
  medical: {
    title: 'Medical information',
    restricted: 'Restricted — medical information',
    hidden:
      'Medical details of injured persons are visible only to authorised roles (by default the Master, the DPA and the company administrator).',
    entry:
      'Medical details are entered by an authorised person (e.g. the Master) after the report is submitted.',
    notice:
      'Health data is special category personal data (GDPR Art. 9, KVKK Art. 6, MLC 2006 A4.1). Write it only in the medical fields, not in the description or chronology.',
  },
  pdf: {
    vessel: 'Vessel',
    type: 'Report type',
    occurred: 'Date / time of event',
    status: 'Status',
    responsible: 'Responsible',
    targetDate: 'Target date',
    investigation: 'Investigation',
    medicalRestricted:
      'Medical details are restricted and not printed for this user (GDPR Art. 9; MLC 2006 A4.1).',
    findings: 'Findings / PSC remarks',
    no: '#',
    finding: 'Finding',
    regulation: 'Regulation',
    code: 'Code / Action',
    due: 'Due',
    state: 'Status',
    actions: 'Corrective actions',
    kind: 'Type',
    action: 'Action',
    completion: 'Completed',
    cancelled: 'Cancelled',
    notification: 'Notification',
    decision: 'Decision',
    reason: 'Reason',
    recipient: 'Recipient',
    name: 'Name',
    notifiedAt: 'Notified at',
    reference: 'Reference',
    links: 'Links',
    equipment: 'Equipment',
    workOrder: 'Work order',
    defect: 'Defect',
    certificate: 'Certificate',
    formRecord: 'Form record',
    parent: 'Parent event',
    submitted: 'Reported',
    reviewed: 'Master review',
    // Kaptanın ilk değerlendirmesini yazan (ADR 0127 §4)
    assessedBy: 'Master’s initial assessment written by',
    closureSubmitted: 'Closure submitted (rev. {{revision}})',
    closureApproved: 'Closure approved',
    closureReturned: 'Closure returned',
    reopened: 'Reopened',
    office: 'office',
  },
};

export const hseExtraTr: typeof hseExtraEn = {
  medical: {
    title: 'Sağlık bilgisi',
    restricted: 'Gizli — sağlık bilgisi',
    hidden:
      'Yaralıların sağlık bilgisi yalnız yetkili rollere görünür (varsayılan: kaptan, DPA ve şirket yöneticisi).',
    entry:
      'Sağlık bilgisi rapor gönderildikten sonra yetkili kişi (ör. kaptan) tarafından girilir.',
    notice:
      'Sağlık verisi özel nitelikli kişisel veridir (GDPR md. 9, KVKK md. 6, MLC 2006 A4.1). Yalnız sağlık alanlarına yazın; açıklama ya da kronolojiye yazmayın.',
  },
  pdf: {
    vessel: 'Gemi',
    type: 'Rapor türü',
    occurred: 'Olay tarihi / saati',
    status: 'Durum',
    responsible: 'Sorumlu',
    targetDate: 'Hedef tarih',
    investigation: 'İnceleme',
    medicalRestricted:
      'Sağlık bilgisi gizlidir; bu kullanıcı için basılmadı (GDPR md. 9; MLC 2006 A4.1).',
    findings: 'Bulgular / PSC remark’ları',
    no: '#',
    finding: 'Bulgu',
    regulation: 'Mevzuat',
    code: 'Kod / Önlem',
    due: 'Hedef',
    state: 'Durum',
    actions: 'Düzeltici faaliyetler',
    kind: 'Tür',
    action: 'Faaliyet',
    completion: 'Tamamlandı',
    cancelled: 'İptal',
    notification: 'Bildirim',
    decision: 'Karar',
    reason: 'Gerekçe',
    recipient: 'Alıcı',
    name: 'Ad',
    notifiedAt: 'Bildirim zamanı',
    reference: 'Referans',
    links: 'Bağlantılar',
    equipment: 'Ekipman',
    workOrder: 'İş emri',
    defect: 'Arıza',
    certificate: 'Sertifika',
    formRecord: 'Form kaydı',
    parent: 'Ana olay',
    submitted: 'Raporlandı',
    reviewed: 'Kaptan incelemesi',
    // Kaptanın ilk değerlendirmesini yazan (ADR 0127 §4)
    assessedBy: 'Kaptanın ilk değerlendirmesini yazan',
    closureSubmitted: 'Kapanışa gönderildi (rev. {{revision}})',
    closureApproved: 'Kapanış onaylandı',
    closureReturned: 'Kapanış iade edildi',
    reopened: 'Yeniden açıldı',
    office: 'ofis',
  },
};

export const portCallPdfEn = {
  title: 'Port Call — {{port}}',
  status: 'Status',
  times: 'Times',
  cancellation: 'Cancellation',
  cancelledAt: 'Cancelled at',
  reason: 'Reason',
  documents: 'Related documents kept with the record: {{count}}',
  recorded: 'Recorded on board',
  updated: 'Last updated',
};

export const portCallPdfTr: typeof portCallPdfEn = {
  title: 'Liman Araması — {{port}}',
  status: 'Durum',
  times: 'Zamanlar',
  cancellation: 'İptal',
  cancelledAt: 'İptal zamanı',
  reason: 'Gerekçe',
  documents: 'Kayıtla saklanan ilgili belgeler: {{count}}',
  recorded: 'Gemide kaydedildi',
  updated: 'Son güncelleme',
};

export const companyNcEn = {
  segment: {
    label: 'Records',
    vessel: 'Vessel reports',
    company: 'Company (office)',
  },
  sources: {
    internal_audit: 'Internal Audit (ISM 12)',
    external_audit: 'External Audit (DOC)',
    management_review: 'Management Review',
    flag_inspection: 'Flag Inspection',
    class_survey: 'Class Survey',
    vetting: 'Vetting / Customer',
    customer_complaint: 'Customer Complaint',
    self_detected: 'Self Detected',
    other: 'Other',
  },
  departments: {
    management: 'Management',
    technical: 'Technical',
    marine: 'Marine / Operations',
    crewing: 'Crewing',
    hse: 'HSE / Quality',
    purchasing: 'Purchasing',
    accounts: 'Accounts',
    it: 'IT',
    office: 'Office (general)',
    other: 'Other',
  },
  statuses: {
    open: 'Open',
    action_assigned: 'Action Assigned',
    in_progress: 'In Progress',
    pending_verification: 'Pending Verification',
    closed: 'Closed',
    reopened: 'Reopened',
  },
  noVessel: 'Company (no vessel)',
  list: {
    title: 'Company Non-Conformities',
    description:
      'Non-conformities found in the office: internal audits, external audits and management reviews (ISM Code 9 and 12).',
    new: 'New Company NC',
    search: 'Search',
    searchPlaceholder: 'Search number, title, vessel...',
    status: 'Status',
    allStatuses: 'All statuses',
    table: 'Company non-conformities',
    error: 'Company non-conformities could not be loaded.',
    emptyTitle: 'No company non-conformities yet',
    emptyHint:
      'Select “New Company NC” to record a finding of an internal audit, external audit or management review.',
    noMatch: 'No record matches the filters.',
    open: 'Open {{no}}',
    showing: 'Showing {{from}} - {{to}} of {{total}}',
    pagination: 'Company non-conformities pages',
    overdue: 'Overdue',
    columns: {
      no: 'No',
      detected: 'Detected',
      vessel: 'Vessel',
      source: 'Source',
      ncClass: 'Class',
      title: 'Title',
      status: 'Status',
      responsible: 'Responsible',
      target: 'Target Date',
    },
    kpis: {
      total: 'Total',
      open: 'Open',
      pendingVerification: 'To Verify',
      overdue: 'Overdue',
      closed: 'Closed',
    },
  },
  record: {
    newTitle: 'New Company Non-Conformity',
    breadcrumb: 'Breadcrumb',
    loading: 'Loading record...',
    loadError: 'The record could not be loaded.',
    retry: 'Retry',
    save: 'Save',
    create: 'Open Non-Conformity',
    saved: 'Saved.',
    fixFields: 'Check the marked fields.',
    generalTitle: 'General Information',
    investigationTitle: 'Root Cause and Responsibility',
    officeOnly:
      'Company records are kept in the office and are not sent to the vessel. To involve a vessel, use “Request report” on the vessel reports.',
  },
  fields: {
    title: 'Title',
    description: 'Description of the non-conformity',
    source: 'Source',
    ncClass: 'Classification',
    smsReference: 'ISM / SMS reference',
    department: 'Department',
    detectedOn: 'Detected on',
    detectedBy: 'Detected by',
    vessel: 'Related vessel',
    immediateAction: 'Immediate correction',
    rootCause: 'Root cause',
    verificationNote: 'Verification of effectiveness',
    responsible: 'Responsible',
    targetDate: 'Target date',
    status: 'Status',
  },
  actions: {
    title: 'Corrective Actions',
    add: 'Add Action',
    none: 'No actions yet. Add a correction, corrective or preventive action.',
    kind: 'Type',
    description: 'Description',
    responsible: 'Responsible',
    due: 'Due date',
    status: 'Status',
    completion: 'Completed',
    cancelled: 'Cancelled',
    start: 'Start',
    complete: 'Complete',
    cancel: 'Cancel action',
    edit: 'Edit',
    addTitle: 'Add Corrective Action',
    editTitle: 'Edit Action',
    completeTitle: 'Complete Action',
    cancelTitle: 'Cancel Action',
    note: 'What was done (evidence)',
    noteHint: 'Describe the evidence: issued revision, training record, checklist, e-mail...',
    reason: 'Reason',
    save: 'Save',
    close: 'Close',
  },
  closure: {
    title: 'Verification & Closure',
    ready: 'All conditions are met. The record can be verified and closed.',
    gapsTitle: 'Before closing:',
    gaps: {
      ROOT_CAUSE: 'Write the root cause (at least 20 characters).',
      NO_ACTIONS: 'Add at least one action.',
      ACTIONS_OPEN: 'Complete or cancel every action.',
      CORRECTIVE_ACTION: 'Add at least one corrective or preventive action (ISM 9.2).',
      VERIFICATION_NOTE: 'Write the verification of effectiveness.',
    },
    selfClose: 'The person who opened the record cannot close it (four-eyes principle).',
    close: 'Verify and Close',
    reopen: 'Reopen',
    closeTitle: 'Verify and Close',
    closeText:
      'Closing confirms that the actions are complete and effective. The record and its actions cannot be changed afterwards.',
    reopenTitle: 'Reopen Non-Conformity',
    reopenText: 'The record returns to work. The closure stays in the history.',
    note: 'Closing note (optional)',
    reason: 'Reason',
    password: 'Password (optional)',
    passwordRequired: 'Password',
    passwordHint: 'The company requires the password for every approval.',
    confirmClose: 'Close Non-Conformity',
    confirmReopen: 'Reopen',
    cancel: 'Cancel',
    closed: 'Closed.',
    reopened: 'Reopened.',
  },
  events: {
    title: 'History',
    none: 'No closure yet.',
    closed: 'Verified and closed',
    reopened: 'Reopened',
    opened: 'Opened',
  },
  pdf: {
    title: 'Company Non-Conformity',
    opened: 'Opened',
  },
  errors: {
    GENERIC: 'Something went wrong. Try again.',
    NETWORK: 'No connection to the server.',
    UNAUTHENTICATED: 'Your session has ended. Sign in again.',
    FORBIDDEN: 'You are not allowed to do this.',
    NOT_FOUND: 'The record was not found.',
    VALIDATION_FAILED: 'Check the entered values.',
    ROW_VERSION_CONFLICT: 'Someone else changed this record. Reload and try again.',
    INVALID_COMPANY_NC: 'Check the marked fields.',
    INVALID_COMPANY_NC_ACTION: 'Check the action: type, description, responsible and due date.',
    COMPANY_NC_CLOSED: 'The record is closed and cannot be changed.',
    COMPANY_NC_NOT_CLOSED: 'Only a closed record can be reopened.',
    COMPANY_NC_SELF_CLOSE: 'The person who opened the record cannot close it.',
    COMPANY_NC_NOT_CLOSABLE: 'The record cannot be closed yet. See “Before closing”.',
    COMPLETION_NOTE_REQUIRED: 'Describe what was done (evidence).',
    REASON_REQUIRED: 'Write a reason (at least 3 characters).',
    REAUTHENTICATION_REQUIRED: 'Enter your password to confirm.',
    INVALID_PASSWORD: 'The password is not correct.',
    TOO_MANY_ATTEMPTS: 'Too many attempts. Try again later.',
    HSE_ACTION_FINISHED: 'The action is completed or cancelled and cannot be changed.',
    INVALID_ACTION_TRANSITION: 'This step is not possible for the action.',
  },
};

export const companyNcTr: typeof companyNcEn = {
  segment: {
    label: 'Kayıtlar',
    vessel: 'Gemi raporları',
    company: 'Şirket (ofis)',
  },
  sources: {
    internal_audit: 'İç Denetim (ISM 12)',
    external_audit: 'Dış Denetim (DOC)',
    management_review: 'Yönetimin Gözden Geçirmesi',
    flag_inspection: 'Bayrak Denetimi',
    class_survey: 'Klas Sörveyi',
    vetting: 'Vetting / Müşteri',
    customer_complaint: 'Müşteri Şikâyeti',
    self_detected: 'Kendi Tespitimiz',
    other: 'Diğer',
  },
  departments: {
    management: 'Yönetim',
    technical: 'Teknik',
    marine: 'Deniz / Operasyon',
    crewing: 'Personel',
    hse: 'HSE / Kalite',
    purchasing: 'Satın Alma',
    accounts: 'Muhasebe',
    it: 'Bilgi İşlem',
    office: 'Ofis (genel)',
    other: 'Diğer',
  },
  statuses: {
    open: 'Açık',
    action_assigned: 'Faaliyet Atandı',
    in_progress: 'Devam Ediyor',
    pending_verification: 'Doğrulama Bekliyor',
    closed: 'Kapatıldı',
    reopened: 'Yeniden Açıldı',
  },
  noVessel: 'Şirket (gemisiz)',
  list: {
    title: 'Şirket Uygunsuzlukları',
    description:
      'Ofiste bulunan uygunsuzluklar: iç denetim, dış denetim ve yönetimin gözden geçirmesi (ISM Kodu 9 ve 12).',
    new: 'Yeni Şirket Uygunsuzluğu',
    search: 'Ara',
    searchPlaceholder: 'Numara, başlık, gemi ara...',
    status: 'Durum',
    allStatuses: 'Bütün durumlar',
    table: 'Şirket uygunsuzlukları',
    error: 'Şirket uygunsuzlukları yüklenemedi.',
    emptyTitle: 'Henüz şirket uygunsuzluğu yok',
    emptyHint:
      'İç denetim, dış denetim ya da yönetimin gözden geçirmesi bulgusunu kaydetmek için “Yeni Şirket Uygunsuzluğu”nu seçin.',
    noMatch: 'Süzgeçlere uyan kayıt yok.',
    open: '{{no}} kaydını aç',
    showing: '{{total}} kayıttan {{from}} - {{to}}',
    pagination: 'Şirket uygunsuzlukları sayfaları',
    overdue: 'Gecikmiş',
    columns: {
      no: 'No',
      detected: 'Tespit',
      vessel: 'Gemi',
      source: 'Kaynak',
      ncClass: 'Sınıf',
      title: 'Başlık',
      status: 'Durum',
      responsible: 'Sorumlu',
      target: 'Hedef Tarih',
    },
    kpis: {
      total: 'Toplam',
      open: 'Açık',
      pendingVerification: 'Doğrulanacak',
      overdue: 'Geciken',
      closed: 'Kapanan',
    },
  },
  record: {
    newTitle: 'Yeni Şirket Uygunsuzluğu',
    breadcrumb: 'Gezinti yolu',
    loading: 'Kayıt yükleniyor...',
    loadError: 'Kayıt yüklenemedi.',
    retry: 'Yeniden dene',
    save: 'Kaydet',
    create: 'Uygunsuzluğu Aç',
    saved: 'Kaydedildi.',
    fixFields: 'İşaretli alanları kontrol edin.',
    generalTitle: 'Genel Bilgiler',
    investigationTitle: 'Kök Neden ve Sorumluluk',
    officeOnly:
      'Şirket kayıtları ofiste tutulur, gemiye gönderilmez. Bir gemiden iş istemek için gemi raporlarındaki “Rapor iste”yi kullanın.',
  },
  fields: {
    title: 'Başlık',
    description: 'Uygunsuzluğun tanımı',
    source: 'Kaynak',
    ncClass: 'Sınıflandırma',
    smsReference: 'ISM / SMS referansı',
    department: 'Bölüm',
    detectedOn: 'Tespit tarihi',
    detectedBy: 'Tespit eden',
    vessel: 'İlgili gemi',
    immediateAction: 'Anlık düzeltme',
    rootCause: 'Kök neden',
    verificationNote: 'Etkinlik doğrulaması',
    responsible: 'Sorumlu',
    targetDate: 'Hedef tarih',
    status: 'Durum',
  },
  actions: {
    title: 'Düzeltici Faaliyetler',
    add: 'Faaliyet Ekle',
    none: 'Henüz faaliyet yok. Düzeltme, düzeltici ya da önleyici faaliyet ekleyin.',
    kind: 'Tür',
    description: 'Açıklama',
    responsible: 'Sorumlu',
    due: 'Hedef tarih',
    status: 'Durum',
    completion: 'Tamamlandı',
    cancelled: 'İptal',
    start: 'Başlat',
    complete: 'Tamamla',
    cancel: 'Faaliyeti iptal et',
    edit: 'Düzenle',
    addTitle: 'Düzeltici Faaliyet Ekle',
    editTitle: 'Faaliyeti Düzenle',
    completeTitle: 'Faaliyeti Tamamla',
    cancelTitle: 'Faaliyeti İptal Et',
    note: 'Ne yapıldı (kanıt)',
    noteHint: 'Kanıtı tanımlayın: yayınlanan revizyon, eğitim kaydı, kontrol listesi, e-posta...',
    reason: 'Gerekçe',
    save: 'Kaydet',
    close: 'Kapat',
  },
  closure: {
    title: 'Doğrulama ve Kapanış',
    ready: 'Bütün koşullar sağlandı. Kayıt doğrulanıp kapatılabilir.',
    gapsTitle: 'Kapatmadan önce:',
    gaps: {
      ROOT_CAUSE: 'Kök nedeni yazın (en az 20 karakter).',
      NO_ACTIONS: 'En az bir faaliyet ekleyin.',
      ACTIONS_OPEN: 'Her faaliyeti tamamlayın ya da iptal edin.',
      CORRECTIVE_ACTION: 'En az bir düzeltici ya da önleyici faaliyet ekleyin (ISM 9.2).',
      VERIFICATION_NOTE: 'Etkinlik doğrulamasını yazın.',
    },
    selfClose: 'Kaydı açan kişi kapatamaz (dört göz ilkesi).',
    close: 'Doğrula ve Kapat',
    reopen: 'Yeniden Aç',
    closeTitle: 'Doğrula ve Kapat',
    closeText:
      'Kapanış, faaliyetlerin tamamlandığını ve etkili olduğunu onaylar. Kayıt ve faaliyetleri sonra değiştirilemez.',
    reopenTitle: 'Uygunsuzluğu Yeniden Aç',
    reopenText: 'Kayıt yeniden çalışmaya döner. Kapanış geçmişte kalır.',
    note: 'Kapanış notu (isteğe bağlı)',
    reason: 'Gerekçe',
    password: 'Parola (isteğe bağlı)',
    passwordRequired: 'Parola',
    passwordHint: 'Şirket her onayda parola istiyor.',
    confirmClose: 'Uygunsuzluğu Kapat',
    confirmReopen: 'Yeniden Aç',
    cancel: 'Vazgeç',
    closed: 'Kapatıldı.',
    reopened: 'Yeniden açıldı.',
  },
  events: {
    title: 'Geçmiş',
    none: 'Henüz kapanış yok.',
    closed: 'Doğrulandı ve kapatıldı',
    reopened: 'Yeniden açıldı',
    opened: 'Açıldı',
  },
  pdf: {
    title: 'Şirket Uygunsuzluğu',
    opened: 'Açıldı',
  },
  errors: {
    GENERIC: 'Bir şey ters gitti. Yeniden deneyin.',
    NETWORK: 'Sunucuya bağlantı yok.',
    UNAUTHENTICATED: 'Oturumunuz sona erdi. Yeniden giriş yapın.',
    FORBIDDEN: 'Bunu yapma yetkiniz yok.',
    NOT_FOUND: 'Kayıt bulunamadı.',
    VALIDATION_FAILED: 'Girilen değerleri kontrol edin.',
    ROW_VERSION_CONFLICT: 'Kaydı başka biri değiştirdi. Yeniden yükleyip deneyin.',
    INVALID_COMPANY_NC: 'İşaretli alanları kontrol edin.',
    INVALID_COMPANY_NC_ACTION: 'Faaliyeti kontrol edin: tür, açıklama, sorumlu ve hedef tarih.',
    COMPANY_NC_CLOSED: 'Kayıt kapalı; değiştirilemez.',
    COMPANY_NC_NOT_CLOSED: 'Yalnız kapalı kayıt yeniden açılır.',
    COMPANY_NC_SELF_CLOSE: 'Kaydı açan kişi kapatamaz.',
    COMPANY_NC_NOT_CLOSABLE: 'Kayıt henüz kapatılamaz. “Kapatmadan önce” listesine bakın.',
    COMPLETION_NOTE_REQUIRED: 'Ne yapıldığını (kanıtı) yazın.',
    REASON_REQUIRED: 'Gerekçe yazın (en az 3 karakter).',
    REAUTHENTICATION_REQUIRED: 'Onaylamak için parolanızı girin.',
    INVALID_PASSWORD: 'Parola doğru değil.',
    TOO_MANY_ATTEMPTS: 'Çok fazla deneme. Daha sonra yeniden deneyin.',
    HSE_ACTION_FINISHED: 'Faaliyet tamamlandı ya da iptal edildi; değiştirilemez.',
    INVALID_ACTION_TRANSITION: 'Bu adım faaliyet için mümkün değil.',
  },
};
